import { useState, useEffect } from 'react';
import { getCollection } from '../../services/firestoreService';
import { getAnalyticsEvents } from '../../services/analyticsService';
import { subDays, format } from 'date-fns';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';

interface SecurityEvent {
  id: string;
  event_type: string;
  email?: string;
  ip_address?: string;
  user_agent?: string;
  timestamp: any;
  additional_data?: Record<string, any>;
}

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  color: string;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, subtitle, color }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '1.5rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderLeft: `4px solid ${color}`,
  }}>
    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>{title}</p>
    <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>{value}</h2>
    {subtitle && <p style={{ margin: 0, color: '#999', fontSize: '0.75rem' }}>{subtitle}</p>}
  </div>
);

const SecurityTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [failedLogins, setFailedLogins] = useState<SecurityEvent[]>([]);
  const [adminAccess, setAdminAccess] = useState<SecurityEvent[]>([]);
  const [suspiciousActivity, setSuspiciousActivity] = useState<SecurityEvent[]>([]);
  const [failedLoginsByEmail, setFailedLoginsByEmail] = useState<Map<string, number>>(new Map());
  const [failedLoginsByIP, setFailedLoginsByIP] = useState<Map<string, number>>(new Map());
  const [eventTypeDistribution, setEventTypeDistribution] = useState<Map<string, number>>(new Map());
  const [timelineData, setTimelineData] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get all security events from last 30 days
        const yesterday = subDays(new Date(), 30);
        const events = await getCollection<SecurityEvent>(
          'security_events',
          [
            { field: 'timestamp', operator: '>=', value: yesterday.toISOString() }
          ],
          'timestamp',
          'desc',
          1000
        );
        
        setSecurityEvents(events);

        // Filter by event type
        const failed = events.filter(e => e.event_type === 'login_failed');
        const admin = events.filter(e => e.event_type === 'admin_access');
        const suspicious = events.filter(e => 
          e.event_type === 'suspicious_activity' || 
          e.event_type === 'unauthorized_access' ||
          e.event_type === 'rate_limit_exceeded'
        );

        setFailedLogins(failed);
        setAdminAccess(admin);
        setSuspiciousActivity(suspicious);

        // Count failed logins by email
        const emailCounts = new Map<string, number>();
        failed.forEach(event => {
          const email = event.email || 'unknown';
          emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
        });
        setFailedLoginsByEmail(emailCounts);

        // Count failed logins by IP
        const ipCounts = new Map<string, number>();
        failed.forEach(event => {
          const ip = event.ip_address || 'unknown';
          ipCounts.set(ip, (ipCounts.get(ip) || 0) + 1);
        });
        setFailedLoginsByIP(ipCounts);

        // Count events by type
        const typeCounts = new Map<string, number>();
        events.forEach(event => {
          const type = event.event_type || 'unknown';
          typeCounts.set(type, (typeCounts.get(type) || 0) + 1);
        });
        setEventTypeDistribution(typeCounts);

        // Timeline data (events per day)
        const timeline = new Map<string, number>();
        events.forEach(event => {
          const date = event.timestamp?.toDate 
            ? format(event.timestamp.toDate(), 'yyyy-MM-dd')
            : format(new Date(event.timestamp), 'yyyy-MM-dd');
          timeline.set(date, (timeline.get(date) || 0) + 1);
        });
        setTimelineData(timeline);

        // Also get subscription-related security events
        try {
          const subscriptionEvents = await getAnalyticsEvents(30, 'security', undefined);
          // Could add subscription security metrics here
        } catch (e) {
          console.warn('Could not load subscription security events:', e);
        }
      } catch (error) {
        console.error('Error loading security data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading security data...</p>
      </div>
    );
  }

  // Prepare chart data
  const emailData = {
    labels: Array.from(failedLoginsByEmail.keys()).slice(0, 10),
    datasets: [{
      label: 'Failed Login Attempts',
      data: Array.from(failedLoginsByEmail.values()).slice(0, 10),
      backgroundColor: '#ef4444',
    }],
  };

  const ipData = {
    labels: Array.from(failedLoginsByIP.keys()).slice(0, 10),
    datasets: [{
      label: 'Failed Login Attempts by IP',
      data: Array.from(failedLoginsByIP.values()).slice(0, 10),
      backgroundColor: '#f59e0b',
    }],
  };

  const eventTypeData = {
    labels: Array.from(eventTypeDistribution.keys()),
    datasets: [{
      label: 'Event Count',
      data: Array.from(eventTypeDistribution.values()),
      backgroundColor: '#3b82f6',
    }],
  };

  const timelineChartData = {
    labels: Array.from(timelineData.keys()).sort(),
    datasets: [{
      label: 'Security Events',
      data: Array.from(timelineData.keys())
        .sort()
        .map(date => timelineData.get(date) || 0),
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      tension: 0.4,
    }],
  };

  const suspiciousCount = suspiciousActivity.length;
  const highRiskEmails = Array.from(failedLoginsByEmail.entries())
    .filter(([_, count]) => count >= 5)
    .map(([email, _]) => email);

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Security Monitoring
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <KPICard
          title="Total Security Events"
          value={securityEvents.length}
          subtitle="Last 30 days"
          color="#3b82f6"
        />
        <KPICard
          title="Failed Login Attempts"
          value={failedLogins.length}
          subtitle={failedLogins.length > 10 ? '⚠️ High volume' : 'Normal'}
          color={failedLogins.length > 10 ? '#ef4444' : '#10b981'}
        />
        <KPICard
          title="Suspicious Activity"
          value={suspiciousCount}
          subtitle={suspiciousCount > 0 ? '⚠️ Review needed' : 'All clear'}
          color={suspiciousCount > 0 ? '#ef4444' : '#10b981'}
        />
        <KPICard
          title="Admin Access Events"
          value={adminAccess.length}
          subtitle="Last 30 days"
          color="#6366f1"
        />
        <KPICard
          title="High-Risk Emails"
          value={highRiskEmails.length}
          subtitle={highRiskEmails.length > 0 ? '⚠️ 5+ failed attempts' : 'None'}
          color={highRiskEmails.length > 0 ? '#ef4444' : '#10b981'}
        />
      </div>

      {/* Security Events Timeline */}
      {timelineData.size > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0 }}>Security Events Timeline</h3>
          <LineChart data={timelineChartData} height={300} />
        </div>
      )}

      {/* Event Type Distribution */}
      {eventTypeDistribution.size > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0 }}>Event Type Distribution</h3>
          <BarChart data={eventTypeData} height={300} />
        </div>
      )}

      {/* Failed Logins by Email */}
      {failedLoginsByEmail.size > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0 }}>Failed Login Attempts by Email</h3>
          <BarChart data={emailData} height={300} />
        </div>
      )}

      {/* Failed Logins by IP */}
      {failedLoginsByIP.size > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0 }}>Failed Login Attempts by IP Address</h3>
          <BarChart data={ipData} height={300} />
        </div>
      )}

      {/* High-Risk Accounts */}
      {highRiskEmails.length > 0 && (
        <div style={{
          backgroundColor: '#fef2f2',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #ef4444',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0, color: '#ef4444' }}>⚠️ High-Risk Accounts (5+ Failed Attempts)</h3>
          <ul style={{ margin: '1rem 0', paddingLeft: '1.5rem' }}>
            {highRiskEmails.map((email, index) => (
              <li key={index} style={{ marginBottom: '0.5rem' }}>
                <strong>{email}</strong> - {failedLoginsByEmail.get(email)} failed attempts
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Suspicious Activity Alerts */}
      {suspiciousActivity.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          marginBottom: '2rem',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Suspicious Activity Alerts
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Time</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Event Type</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>IP Address</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {suspiciousActivity.slice(0, 20).map((event, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    {event.timestamp?.toDate 
                      ? format(event.timestamp.toDate(), 'MMM dd, yyyy HH:mm')
                      : format(new Date(event.timestamp), 'MMM dd, yyyy HH:mm')}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      backgroundColor: '#fee2e2',
                      color: '#991b1b',
                      fontSize: '0.75rem',
                    }}>
                      {event.event_type}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{event.email || 'N/A'}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                    {event.ip_address || 'N/A'}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    {JSON.stringify(event.additional_data || {}).substring(0, 50)}...
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Admin Access Logs */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        marginBottom: '2rem',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Admin Access Logs
        </h3>
        {adminAccess.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Time</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>IP Address</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {adminAccess.slice(0, 20).map((event, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    {event.timestamp?.toDate 
                      ? format(event.timestamp.toDate(), 'MMM dd, yyyy HH:mm')
                      : format(new Date(event.timestamp), 'MMM dd, yyyy HH:mm')}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{event.email || 'N/A'}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                    {event.ip_address || 'N/A'}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    {event.additional_data?.action || 'Admin access'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ padding: '1rem', color: '#666' }}>
            No admin access logs found in the last 30 days.
          </p>
        )}
      </div>

      {/* Recent Security Events */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Recent Security Events
        </h3>
        {securityEvents.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Time</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Event Type</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {securityEvents.slice(0, 50).map((event, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    {event.timestamp?.toDate 
                      ? format(event.timestamp.toDate(), 'MMM dd, yyyy HH:mm')
                      : format(new Date(event.timestamp), 'MMM dd, yyyy HH:mm')}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{event.event_type}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{event.email || 'N/A'}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                    {event.ip_address || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ padding: '1rem', color: '#666' }}>
            No security events found in the last 30 days.
          </p>
        )}
      </div>
    </div>
  );
};

export default SecurityTab;
